import React, {
  Fragment,
  cloneElement,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { format } from 'date-fns';
import jsonExport from 'jsonexport/dist';
import { downloadCSV } from 'react-admin';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import {
  useRefresh,
  List,
  BulkDeleteButton,
  TextField,
  FunctionField,
  ChipField,
  TopToolbar,
  ReferenceField,
  Pagination,
  usePermissions,
  ExportButton,
} from 'react-admin';
import {
  Drawer,
  Button,
} from '@material-ui/core';
import { CustomButton } from '../design/material-ui';
import { getChip, constants } from '../utils';
import {
  DateField,
  Datagrid,
  CaseManagerSelect,
} from '../components/common';
import {
  NewEvent,
} from '../components/common/modals';
import { useSidebarOpen } from '../hooks';
import { useStyles } from './auths.styles';
import {
  AuthIcon,
  AddIcon,
} from '../design';
import { AuthShow } from './ancillary_auths';

const { preAuthStatusChoices, whoObtainedChoices } = constants;

const getStatusLabel = (statusId) => {
  const found = preAuthStatusChoices.find(c => c.id === statusId);
  return found ? found.name : statusId || '';
};

const getWhoObtainedLabel = id => {
  const found = whoObtainedChoices.find(c => c.id === id);
  return found ? found.name : id || '';
};

const FacilityLevelActions = ({
  filterManager,
  isViewAdmin,
  caseManager,
  currentSort,
  exporter,
  filterValues,
}) => {
  const classes = useStyles();
  const refresh = useRefresh();
  const [eventOpen, setEventModal] = useState(false);
  return (
    <Fragment>
      <TopToolbar className={clsx(classes.listActionWrapper, classes.flexWrap)}>
        <div className={classes.listActionWrapper}>
          <div style={{ display: 'flex', paddingBottom: 20 }}>
            <span className={classes.title}>Pre-Auth Authorizations</span>
            <span style={{ marginRight: 25 }} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <CustomButton
              Icon={AddIcon}
              color='#1061A0'
              backgroundColor='#EFF4FB'
              label='Add'
              type='button'
              variant='text'
              size='small'
              onClick={() => setEventModal(true)}
              disabled={isViewAdmin}
            />
            <ExportButton
              disabled={isViewAdmin}
              resource='pre-auth-authorizations'
              sort={currentSort}
              filter={{
                ...filterValues,
                division: 'all',
                caseManagerId: caseManager,
                pre_auth: true,
              }}
              exporter={exporter}
              className={classes.exportButton}
              label='Export'
              variant='text'
              size='small'
              color='#1061A0'
            />
          </div>
        </div>
        <div style={{ display: 'flex' }}>
          <CaseManagerSelect filterManager={filterManager} />
        </div>
      </TopToolbar>
      {eventOpen && (
        <NewEvent
          open={eventOpen}
          handleClose={() => setEventModal(false)}
          form='authorization'
          refresh={refresh}
          meta={{ pre_auth: true }}
        />
      )}
    </Fragment>
  );
};

const ListView = ({
  rowClick,
  listRowStyle,
  rowRecord,
  sort,
  setSort,
  ...props
}) => {
  const isSidebarOpen = useSidebarOpen();
  const classes = useStyles({ isSidebarOpen });
  return (
    <div className={classes.listContainer}>
      <Datagrid
        {...props}
        rowClick={rowClick}
        rowStyle={(record, index) => listRowStyle(record, index, rowRecord.id)}
        sort={sort}
        setSort={setSort}
      >
        <FunctionField
          source='insurance_auth_number'
          label='Number'
          render={record => {
            const { insurance_auth_number } = record;
            return (
              <div className={classes.infoContainer}>
                <span style={{ marginRight: 3 }}>{insurance_auth_number}</span>
              </div>
            );
          }}
        />
        <FunctionField
          source='case_name'
          label='Case name'
          sortBy='last_name'
          render={record => {
            const { case_name, case_id } = record;
            if (!case_name || !case_id) return null;
            return (
              <Button
                className={classes.referenceBtn}
                color='primary'
                onClick={e => e.stopPropagation()}
                component={Link}
                to={`/cases/${case_id}/timeline`}
              >
                {case_name}
              </Button>
            );
          }}
        />
        <FunctionField
          label='Status'
          render={record => {
            return (
              <ReferenceField
                source='authorization_status_id'
                reference='auths-status/list'
                link={false}
              >
                <FunctionField
                  source='name'
                  render={record => (
                    <ChipField
                      record={record}
                      source='name'
                      className={clsx(
                        classes.chip,
                        classes[getChip(record.name)],
                      )}
                    />
                  )}
                />
              </ReferenceField>
            );
          }}
        />
        <FunctionField
          source='pre_auth_status'
          label='Pre-Auth Status'
          render={record => getStatusLabel(record.pre_auth_status)}
        />
        <DateField source='pre_auth_status_date' label='Status Date' />
        <FunctionField
          source='pre_auth_who_obtained'
          label='Who Obtained'
          render={record => getWhoObtainedLabel(record.pre_auth_who_obtained)}
        />
        <DateField source='pre_auth_close_date' label='Close Date' />
        <DateField source='start_date' label='Start date' />
        <FunctionField
          source='end_date'
          label='End date'
          render={record => (
            <DateField
              record={record}
              source={record.actual_end_date ? 'actual_end_date' : 'end_date'}
              label='End date'
            />
          )}
        />
        <TextField source='payer_name' label='Payer' />
        <FunctionField
          label='Case Manager'
          render={record => record?.user?.name || ''}
        />
      </Datagrid>
    </div>
  );
};


export const PreAuthList = ({ staticContext, ...props }) => {
  const refresh = useRefresh();
  const classes = useStyles();
  const [asideOpen, toggleAside] = useState(false);
  const [rowRecord, setRowRecord] = useState({});
  const [caseManager, setCaseManager] = useState();
  const [filterValues, setFilterValues] = useState({});
  const [currentSort, setCurrentSort] = useState({
    field: 'start_date',
    order: 'DESC',
  });
  const firstRender = useRef(true);
  const { permissions = '' } = usePermissions();
  const userPermissions = permissions.split(',');
  const isViewAdmin =
    userPermissions?.indexOf('view_admin') > -1 ||
    userPermissions?.indexOf('admin_assistant') > -1;

  const exporter = useCallback(rows => {
    const fieldsForExport = rows.map(row => {
      const {
        insurance_auth_number,
        case_name,
        authorization_status,
        pre_auth_status,
        pre_auth_status_date,
        pre_auth_who_obtained,
        pre_auth_close_date,
        pre_auth_notes,
        start_date,
        end_date,
        payer_name,
        user,
      } = row;
      return {
        insurance_auth_number,
        case_name,
        status: authorization_status?.status,
        pre_auth_status: getStatusLabel(pre_auth_status),
        pre_auth_status_date,
        pre_auth_who_obtained: getWhoObtainedLabel(pre_auth_who_obtained),
        pre_auth_close_date,
        pre_auth_notes,
        start_date,
        end_date,
        payer_name,
        case_manager: user?.name,
      };
    });
    jsonExport(
      fieldsForExport,
      {
        headers: [
          'insurance_auth_number',
          'case_name',
          'status',
          'pre_auth_status',
          'pre_auth_status_date',
          'pre_auth_who_obtained',
          'pre_auth_close_date',
          'pre_auth_notes',
          'start_date',
          'end_date',
          'payer_name',
          'case_manager',
        ],
        rename: [
          'Auth Number',
          'Case Name',
          'Auth Status',
          'Pre-Auth Status',
          'Status Date',
          'Who Obtained',
          'Close Date',
          'Notes',
          'Start Date',
          'End Date',
          'Payer',
          'Case Manager',
        ],
      },
      (err, csv) => {
        downloadCSV(csv, `pre-auth-${format(new Date(), 'MM-dd-yyyy')}`);
      },
    );
  }, []);

  useEffect(() => {
    toggleAside(false);
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    refresh();
  }, [props.location.pathname, refresh]);

  const rowClick = (id, basePath, record) => {
    setRowRecord({ ...record });
    toggleAside(true);
    return null;
  };

  const listRowStyle = (record, index, activeId) => {
    if (record && record.id === activeId && asideOpen) {
      return { backgroundColor: '#EFF4FB' };
    }
  };

  const filterManager = useCallback(value => {
    toggleAside(false);
    setCaseManager(value);
    setFilterValues(prev => ({
      ...prev,
      caseManagerId: value,
    }));
  }, []);

  const handleSort = useCallback((field, order) => {
    setCurrentSort({ field, order });
  }, []);

  return (
    <Fragment>
      <List
        empty={false}
        {...props}
        exporter={false}
        bulkActionButtons={
          !isViewAdmin ? <BulkDeleteButton /> : false
        }
        className={clsx(classes.list, {
          [classes.listWithDrawer]: asideOpen,
        })}
        actions={
          <FacilityLevelActions
            filterManager={filterManager}
            isViewAdmin={isViewAdmin}
            caseManager={caseManager}
            currentSort={currentSort}
            exporter={exporter}
            filterValues={filterValues}
          />
        }
        filter={{
          division: 'all',
          caseManagerId: caseManager,
          pre_auth: true,
        }}
        pagination={
          <Pagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            style={{ display: 'flex' }}
          />
        }
      >
        <ListView
          empty={false}
          rowClick={rowClick}
          listRowStyle={listRowStyle}
          rowRecord={rowRecord}
          sort={currentSort}
          setSort={handleSort}
        />
      </List>
      <Drawer
        variant='persistent'
        open={asideOpen}
        anchor='right'
        elevation={16}
        PaperProps={{ square: true }}
        classes={{
          paperAnchorDockedRight: clsx(classes.aside),
        }}
      >
        {asideOpen
          ? cloneElement(<AuthShow />, {
              ...props,
              id: rowRecord.id,
              closeAside: () => toggleAside(false),
              resource: 'pre-auth-authorizations',
              classes: classes,
              residentId: rowRecord.v_case?.resident_id,
              isViewAdmin: isViewAdmin,
            })
          : null}
      </Drawer>
    </Fragment>
  );
};

export const preAuthResource = {
  name: 'pre-auth-authorizations',
  list: PreAuthList,
  icon: AuthIcon,
};
